import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, FileText, CheckSquare } from 'lucide-react';
import { useDebouncedSearch } from '../../hooks/useSearch';
import { SearchResult } from '../../types/search';
import { Modal } from '../common';

interface SearchModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when modal should close */
  onClose: () => void;
}

/**
 * Type badge component
 */
function TypeBadge({ type }: { type: 'task' | 'page' }) {
  const styles =
    type === 'task'
      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
      : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300';

  const Icon = type === 'task' ? CheckSquare : FileText;
  const label = type === 'task' ? 'Task' : 'Page';

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${styles}`}
      aria-label={`Type: ${label}`}
    >
      <Icon className="w-3 h-3" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * Individual search result item for modal
 */
function ResultItem({
  result,
  isSelected,
  onClick,
  id,
}: {
  result: SearchResult;
  isSelected: boolean;
  onClick: () => void;
  id: string;
}) {
  const itemRef = useRef<HTMLButtonElement>(null);

  // Scroll into view when selected
  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <button
      ref={itemRef}
      id={id}
      onClick={onClick}
      className={`w-full text-left px-4 py-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 ${
        isSelected ? 'bg-blue-50 dark:bg-blue-900/30 border-l-2 border-l-blue-500' : ''
      }`}
      role="option"
      aria-selected={isSelected}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <TypeBadge type={result.type} />
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex-1">
              {result.title}
            </h4>
            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
              {Math.round(result.score * 100)}% match
            </span>
          </div>
          {result.excerpt && (
            <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 mt-1">
              {result.excerpt}
            </p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{result.url}</p>
        </div>
      </div>
    </button>
  );
}

/**
 * Full-screen search modal with enhanced results display
 * Triggered by Cmd/Ctrl + K
 */
export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const {
    query,
    setQuery,
    results,
    isSearching,
    isIndexReady,
    isLoading,
  } = useDebouncedSearch(200); // Faster debounce for modal

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(-1);
    }
  }, [isOpen, setQuery]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(results.length > 0 ? 0 : -1);
  }, [results]);

  // Handle navigation to result
  const handleSelectResult = useCallback(
    (result: SearchResult) => {
      navigate(result.url);
      onClose();
    },
    [navigate, onClose]
  );

  // Arrow keys / Enter — Escape and focus trap are handled by Modal.
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (results.length > 0) {
            setSelectedIndex((prev) =>
              prev < results.length - 1 ? prev + 1 : 0
            );
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (results.length > 0) {
            setSelectedIndex((prev) =>
              prev > 0 ? prev - 1 : results.length - 1
            );
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            handleSelectResult(results[selectedIndex]);
          }
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, handleSelectResult]);

  // Generate live region message
  const liveMessage = isSearching
    ? 'Searching...'
    : results.length > 0
    ? `${results.length} result${results.length !== 1 ? 's' : ''} found`
    : query.trim() && isIndexReady
    ? 'No results found'
    : '';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Search"
      hideTitle
      size="lg"
      initialFocusRef={inputRef}
    >
      <div className="flex flex-col h-full">
        {/* Live region for screen reader announcements */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {liveMessage}
        </div>

        {/* Search input header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          {isSearching || isLoading ? (
            <Loader2 className="w-5 h-5 text-gray-400 dark:text-gray-500 animate-spin flex-shrink-0" aria-hidden="true" />
          ) : (
            <Search className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" aria-hidden="true" />
          )}
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks and wiki pages..."
            className="flex-1 text-base sm:text-lg bg-transparent outline-none placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 min-h-[44px]"
            aria-label="Search tasks and wiki pages"
            aria-busy={isSearching}
          />
        </div>

        {/* Results area */}
        <div
          className="flex-1 sm:flex-none sm:max-h-[60vh] overflow-auto"
          aria-busy={isSearching}
        >
          {/* Loading state */}
          {isLoading && (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400" role="status">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" aria-hidden="true" />
              <p>Loading search index...</p>
            </div>
          )}

          {/* Index not ready */}
          {!isLoading && !isIndexReady && (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400" role="status">
              <p>Search index is initializing...</p>
            </div>
          )}

          {/* Ready but no query */}
          {!isLoading && isIndexReady && !query.trim() && (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" aria-hidden="true" />
              <p className="text-lg mb-1">Search the toolkit</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Find tasks, wiki pages, and more
              </p>
            </div>
          )}

          {/* No results */}
          {!isLoading && isIndexReady && query.trim() && results.length === 0 && !isSearching && (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400" role="status">
              <p className="text-lg mb-1">No results found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Try different keywords or check your spelling
              </p>
            </div>
          )}

          {/* Results list */}
          {!isLoading && results.length > 0 && (
            <div
              role="listbox"
              aria-label={`Search results, ${results.length} ${results.length === 1 ? 'result' : 'results'}`}
              aria-activedescendant={selectedIndex >= 0 ? `search-result-${selectedIndex}` : undefined}
            >
              {results.map((result, index) => (
                <ResultItem
                  key={`${result.type}-${result.id}`}
                  result={result}
                  isSelected={selectedIndex === index}
                  onClick={() => handleSelectResult(result)}
                  id={`search-result-${index}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
          <div className="flex items-center gap-4">
            <span>
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                ↑
              </kbd>{' '}
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                ↓
              </kbd>{' '}
              to navigate
            </span>
            <span>
              <kbd className="px-2 py-0.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                Enter
              </kbd>{' '}
              to select
            </span>
            <span>
              <kbd className="px-2 py-0.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                Esc
              </kbd>{' '}
              to close
            </span>
          </div>
          {results.length > 0 && (
            <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </Modal>
  );
}
